import React, { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import AssetPreviewItem from "./AssetPreviewItem";
import AssetsLibraryUtilities from "./AssetsLibraryUtilities";
import AssetsManager from "../../classes/AssetsManager";
import { ApplicationModuleTypes, AssetTypes } from "../../globals";
import Grid from "../layout/Grid";
import Panel from "../layout/Panel";
import PlaceholderContent from "../layout/PlaceholderContent";
import MaterialEditor from "../materials/MaterialEditor";
import UIEditor from "../uisystem/UIEditor";
import { useTypedDispatch, useTypedSelector } from "../../state/hooks";
import { IAssetInfo } from "../../interfaces/Assets";
import Button from "../layout/Button";
import store, { StoreActions } from "../../state/store";
import { openWindow } from "../../classes/ShortcutHandler";
import { IVisageTemplate, VisageEditorWindowModes } from "../../interfaces/VisageUI";
import Spacer from "../layout/Spacer";
import VisageTemplatePreview from "../visageUI/VisageTemplatePreview";
import { TooltipKeys } from "../tooltips/TooltipKeys";
import UITemplateTypes from "../uisystem/templates/types";
import useCurrentStep from "../reactUtils/useCurrentStep";
export function AssetPreviewDisplay(props)
{
	const dispatch = useTypedDispatch();
	const currentStep = useCurrentStep();

	const selectedAssets = useTypedSelector(state => state.assetModule.selectedAssets);
	const slideMaster = useTypedSelector(state => state.uiModule.slideMaster);
	const editingSlide = useTypedSelector(state => state.uiModule.editingSlide);
	const previewItemScale = useTypedSelector(state => state.assetModule.previewScale);

	const allAssetsFlat = AssetsManager.getAllAssetsFlat();

	const rootRef = useRef<HTMLDivElement>(null);

	// Add effect to scroll to selected item
	const gridRefCallback = useCallback((gridRef: HTMLDivElement | null) => 
	{
		if (props.selectedAsset) 
		{
			
			// Use setTimeout to ensure DOM is fully rendered
			setTimeout(() => 
			{
				const selectedElement = gridRef?.querySelector(`[data-asset-id="${props.selectedAsset}"]`);
				
				if (selectedElement) 
				{
					selectedElement.scrollIntoView({ behavior: 'instant', block: 'center' });
				}
			}, 100); // Small delay to ensure rendering is complete
		}
	}, []);

	function generateAssetPreviews()
	{
		if(!props.assets && !selectedAssets?.length || !rootRef.current) return;
		
		// special-case for single asset selection
		if(!props.assets && selectedAssets.length === 1)
		{
			const assetInfo = allAssetsFlat[selectedAssets[0]];
			const assetProps = AssetsManager.getPropsForAsset(selectedAssets[0]);
			if(!assetInfo || !assetProps) return;

			switch(assetProps.type)
			{
				case AssetTypes.UITemplate:
					const slideData = slideMaster[assetProps.nodeId];
					if (!editingSlide)
					{
						return <UIEditor key={assetProps.nodeId} id={assetProps.nodeId} slide={slideData} />;
					}
					else
					{
						if (editingSlide && editingSlide === assetProps.nodeId)
						{
							return <UIEditor key={assetProps.nodeId} id={assetProps.nodeId} slide={slideData} />;
						}
					}
					break;
				case AssetTypes.Material:
					return <MaterialEditor id={assetProps.nodeId} key={assetProps.nodeId} scale={previewItemScale} />;
				case AssetTypes.VisageTemplate:
					const disabled = currentStep.isDefault() || currentStep.isTrackersStep();
					return (
						<Panel className={"visageTemplateAssetPreview"}>
							<div className={"propertyGroup"}>
								<Button key={assetProps.nodeId}
									label={"Edit Template"}
									disabled={disabled}
									data-tooltip={disabled ? TooltipKeys.EditTemplateDisabled : ""}
									onClick={() => 
									{
										openWindow(ApplicationModuleTypes.VisageEditorWindow);
										dispatch(StoreActions.visage.setEditorMode(VisageEditorWindowModes.Template));
										dispatch(StoreActions.visage.setTemplateEditTarget(assetProps.nodeId));
										dispatch(StoreActions.visage.setTemplateEditorTargetStep("")); // when editing a template from the asset preview, we don't want to edit a specific step

										const template: IVisageTemplate = store.getState().visageModule.uiTemplateData[assetProps.nodeId];
										if(template.defaultTemplateID === UITemplateTypes.QUIZ_RESULTS)
										{
											dispatch(StoreActions.ui.setBackupSlide({ ...store.getState().uiModule.slideMaster[UITemplateTypes.QUIZ_RESULTS] }));
											dispatch(StoreActions.ui.setEditingSlide(UITemplateTypes.QUIZ_RESULTS));
										}
									}}
								/>
							</div>
							<Spacer size={{ y: "10px" }} />
							<VisageTemplatePreview templateId={assetProps.assetId} wrapperRect={rootRef.current.getBoundingClientRect()} />
						</Panel>
					);
			}
		}

		function previewItemFor(assetId: string)
		{
			const assetInfo = allAssetsFlat[assetId];
			if(!assetInfo) return null;

			return <AssetPreviewItem
				assetId={assetId}
				key={assetId}
				scale={props.scale ?? previewItemScale}
				onClick={() => props.onClickCallback?.(assetId)}
				onDoubleClick={() => props.overrideDoubleClick?.(assetId) ?? AssetsLibraryUtilities.selectAssets([assetId], { autoScroll: true, autoExpand: true, once: true })}
				draggable={!props.noDrag && !assetInfo.shared}
				listItem={props.listItems}
				selected={props.selectedAsset === assetId ? "selected" : undefined}
				data-asset-id={assetId}
				selectable={props.selectable}
				hideFolderNames={props.hideFolderNames}
			/>
		}

		const output = new Array<JSX.Element | null>();

		const assets = props.assets ?? selectedAssets;
		
		// when multiple assets are selected, display their previews
		for(const assetId of assets)
		{
			const assetProps = AssetsManager.getPropsForAsset(assetId);
			if(!assetProps) continue;

			if(assetProps.type === AssetTypes.Folder)
			{
				for (const assetInfo of Object.values<IAssetInfo>(allAssetsFlat))
				{
					if(!assetInfo) continue;

					if (assetInfo.folder === assetProps.nodeId)
					{
						output.push(previewItemFor(assetInfo.id));
					}
				}
			}
			else
			{
				output.push(previewItemFor(assetId));
			}
		}

		return output.filter(x => x !== null);
	}

	return (
		<Panel className={"solid"}
			data-name={"asset-preview-content-root"}
			ref={rootRef}
			style={{
				flexGrow: 100,
			}}
		>
			{
				!props.assets && !selectedAssets?.length ?
					<PlaceholderContent data-name={"asset-preview-empty-content-placeholder"}>
						Select Asset to Preview
					</PlaceholderContent>
					:
					// TODO: this is a garbage bin, but it will be gone with the new ui system so bear with it for now
					<>
						{
							props.assets && 
							<div className={"selectableAssetPreview"} 
								data-status={props.selectedAsset === "" ? "selected" : undefined} style={{margin: "4px", padding: "8px"}}
								onClick={() => props.onClickCallback?.("")}
								onDoubleClick={() => props.overrideDoubleClick?.("")}
							>
								- None -
							</div>}
						{
							!props.selectable && selectedAssets.length === 1 && AssetsManager.getPropsForAsset(selectedAssets[0])?.type === AssetTypes.UITemplate ?
								<div className={"draggableWindowContent"}>
									{generateAssetPreviews()}
								</div>
								:
								<Grid data-name={"asset-preview-content-grid"}
									ref={gridRefCallback}
								>
									{generateAssetPreviews()}
								</Grid>}
					</>
			}
		</Panel>
	);
}
